import { Box, Button } from '@factorialco/deprecated-design-system'
import { ApprovalsGroupFragment, UpdateApprovalGroupDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  group: ApprovalsGroupFragment
}

type ApprovalGroupFormData = {
  name: string
  description: string
}

const EditGroupModal = ({ onClose, group }: Props) => {
  const [updateApprovalGroup, { loading: updatingApprovalGroup }] = useMutation(
    UpdateApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.updateApprovalsApprovalGroup,
    }
  )

  const form = useForm<ApprovalGroupFormData>({
    defaultValues: {
      name: group.name,
      description: group.description || '',
    },
    validate: (data) => {
      const errors: FormErrors<ApprovalGroupFormData> = {}

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await updateApprovalGroup({
        variables: {
          id: group.id,
          name: values.name,
          description: values.description,
          autoApproved: group.autoApproved,
        },
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors(errors)
      })

      if (handled.success) {
        track(trackingEvents.approvals.modifyApprovalGroupSubmit)
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.approvals.approval_group.flash_messages.successful_update'),
          time: 2000,
        })
        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.approvals.approval_group.flash_messages.error_update'),
          time: 2000,
        })
      }

      return handled
    },
  })

  useEffect(() => {
    track(trackingEvents.approvals.modifyApprovalGroupStart)
  }, [])

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('settings.approvals.approval_group.details.title')}
      description={i18n.t('settings.approvals.approval_group.new.modal_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.dirty || updatingApprovalGroup} submit>
            {i18n.t('settings.approvals.approval_group.new.save')}
          </Button>
        </>
      }
    >
      <Box padding="s2" gap="s16">
        <TextField
          label={i18n.t('settings.approvals.approval_group.new.title')}
          placeholder={i18n.t('settings.approvals.approval_group.new.title')}
          value={form.values.name}
          onChange={(newValue) => form.setFieldValue('name', newValue)}
          error={form.touched.name ? form.errors.name : undefined}
        />
        <TextArea
          label={i18n.t('settings.approvals.approval_group.new.description')}
          placeholder={i18n.t('settings.approvals.approval_group.new.description')}
          value={form.values.description}
          onChange={(newValue) => form.setFieldValue('description', newValue)}
          error={form.touched.description ? form.errors.description : undefined}
        />
      </Box>
    </Modal>
  )
}

export default EditGroupModal
